import type { GridRowSelectionModel } from '@repo/go-table';
import { action, computed, makeObservable, observable, reaction, type IReactionDisposer } from 'mobx';

import { DEFAULT_TABLE_SETTINGS_MODEL } from './consts';
import type { TrajectoryGraphStore } from './features/trajectory-graph/trajectory-graph.store';
import { DisplayManager } from './features/trajectory-table/display-manager';
import type { TrajectoryService } from './service/trajectory-service';
import type { TTrajectoryRowDeleteButtonState } from './types/external-services';
import type { TrajectoryTableSettingsModel } from './types/host-services';
import type { TrajectoryExpandState, TrajectoryWidgetStateParams } from './types/state';

export class TrajectoryUiStore {
  @observable expandState: TrajectoryExpandState = 'default';
  @observable selectedRowIds: GridRowSelectionModel = [];
  @observable private _isTableSettingsOpen = false;
  @observable private _isTableCheckboxesVisible = false;
  @observable private _isTrajectoryListOpen = false;
  @observable activeSettingsTab: 'charts' | 'table' = 'table';
  @observable private _isEditing = false;
  /** `undefined` = uncontrolled; boolean = controlled from host props. Must be observable for MobX. */
  @observable private controlledTableSettingsOpen: boolean | undefined = undefined;
  @observable private controlledTableCheckboxesVisible: boolean | undefined = undefined;
  @observable private controlledTrajectoryListOpen: boolean | undefined = undefined;
  @observable.ref displayManager: DisplayManager;
  /** Portal root for settings Drawer; null/undefined → document.body. */
  @observable.ref settingsDrawerContainer: Element | null = null;
  private readonly trajectoryService: TrajectoryService;

  private onStateChange?: (state: TrajectoryWidgetStateParams) => void;
  private graphStore: TrajectoryGraphStore | null = null;
  private disposers: IReactionDisposer[] = [];
  @observable.ref private onRowDeleteButtonStateChange: ((state: TTrajectoryRowDeleteButtonState) => void) | null =
    null;

  private onTableSettingsOpenChange?: (open: boolean) => void;
  private onTableCheckboxesVisibleChange?: (visible: boolean) => void;
  private onTrajectoryListOpenChange?: (open: boolean) => void;

  constructor(trajectoryService: TrajectoryService, initialState?: TrajectoryWidgetStateParams) {
    this.trajectoryService = trajectoryService;
    this.expandState = initialState?.expandState ?? 'default';
    this.displayManager = new DisplayManager({ ...DEFAULT_TABLE_SETTINGS_MODEL, ...initialState?.tableSettings }, () =>
      this.emitStateChange(),
    );

    makeObservable(this);
  }

  @action.bound
  bindGraphStore(graphStore: TrajectoryGraphStore): void {
    this.graphStore = graphStore;
  }

  @computed
  get isUiLocked(): boolean {
    return this.trajectoryService.isSaving;
  }

  @computed
  get isEditing(): boolean {
    return this._isEditing;
  }

  @action.bound
  setIsEditing(isEditing: boolean): void {
    this._isEditing = isEditing;
  }

  @action.bound
  setSettingsDrawerContainer(container: Element | null): void {
    this.settingsDrawerContainer = container;
  }

  @action.bound
  setExternalCallbacks(options: {
    onStateChange?: (state: TrajectoryWidgetStateParams) => void;
    isTableSettingsOpen?: boolean;
    isTableCheckboxesVisible?: boolean;
    isTrajectoryListOpen?: boolean;
    onTableSettingsOpenChange?: (open: boolean) => void;
    onTableCheckboxesVisibleChange?: (visible: boolean) => void;
    onTrajectoryListOpenChange?: (open: boolean) => void;
  }): void {
    this.onStateChange = options.onStateChange;
    this.controlledTableSettingsOpen = options.isTableSettingsOpen;
    this.controlledTableCheckboxesVisible = options.isTableCheckboxesVisible;
    this.controlledTrajectoryListOpen = options.isTrajectoryListOpen;
    this.onTableSettingsOpenChange = options.onTableSettingsOpenChange;
    this.onTableCheckboxesVisibleChange = options.onTableCheckboxesVisibleChange;
    this.onTrajectoryListOpenChange = options.onTrajectoryListOpenChange;
  }

  @action.bound
  setRowDeleteButtonStateChange(callback: ((state: TTrajectoryRowDeleteButtonState) => void) | null): void {
    this.onRowDeleteButtonStateChange = callback;
  }

  @computed
  get isTableSettingsOpen(): boolean {
    return this.controlledTableSettingsOpen ?? this._isTableSettingsOpen;
  }

  @computed
  get isTableCheckboxesVisible(): boolean {
    return this.controlledTableCheckboxesVisible ?? this._isTableCheckboxesVisible;
  }

  @computed
  get isTrajectoryListOpen(): boolean {
    return this.controlledTrajectoryListOpen ?? this._isTrajectoryListOpen;
  }

  @action.bound
  setSelectedRowIds(rowSelectModel: GridRowSelectionModel): void {
    this.selectedRowIds = rowSelectModel;
  }

  @action.bound
  deleteSelectedRows(): void {
    const ids = this.selectedRowIds.map((rowId) => String(rowId));
    this.trajectoryService.deleteMainPoints(ids);
    this.setSelectedRowIds([]);
  }

  @action.bound
  setExpandState(expandState: TrajectoryExpandState): void {
    this.expandState = expandState;
    this.emitStateChange();
  }

  @action.bound
  setTableSettingsOpen(open: boolean): void {
    if (this.controlledTableSettingsOpen === undefined) {
      this._isTableSettingsOpen = open;
    }

    this.onTableSettingsOpenChange?.(open);
  }

  @action.bound
  setTableCheckboxesVisible(visible: boolean): void {
    if (this.controlledTableCheckboxesVisible === undefined) {
      this._isTableCheckboxesVisible = visible;
    }

    this.onTableCheckboxesVisibleChange?.(visible);
  }

  @action.bound
  setTrajectoryListOpen(open: boolean): void {
    if (open && this.isUiLocked) {
      return;
    }

    if (this.controlledTrajectoryListOpen === undefined) {
      this._isTrajectoryListOpen = open;
    }

    this.onTrajectoryListOpenChange?.(open);
  }

  @action.bound
  setActiveSettingsTab(tab: 'charts' | 'table'): void {
    this.activeSettingsTab = tab;
  }

  getSnapshot(): TrajectoryWidgetStateParams {
    return {
      expandState: this.expandState,
      chartDimension: this.graphStore?.chartDimension ?? '2d',
      trajectory3dViewControl: this.graphStore?.trajectory3dViewControl ?? null,
      chart2dSettings: this.graphStore?.chart2dSettings,
      verticalDatum: this.graphStore?.verticalDatum ?? 'tvd',
      tableSettings: {
        ...DEFAULT_TABLE_SETTINGS_MODEL,
        ...this.displayManager.settings,
      } as TrajectoryTableSettingsModel,
    };
  }

  @action.bound
  emitStateChange(): void {
    this.onStateChange?.(this.getSnapshot());
  }

  /** Persist 3D camera into host snapshot on graph unmount (C2). */
  @action.bound
  flushCameraState(): void {
    this.emitStateChange();
  }

  init = (): VoidFunction => {
    this.disposers.push(
      reaction(
        () => this.trajectoryService.mainFactTrajectory?.entityId,
        () => {
          this.setSelectedRowIds([]);
        },
      ),
      reaction(
        () => ({
          isEditing: this._isEditing,
          mdTieOn: this.trajectoryService.mainFactTrajectory?.mdTieOnMeasuredDepth ?? null,
          source: this.trajectoryService.mainFactTrajectory?.data.source ?? null,
          selectedRowIds: this.selectedRowIds,
        }),
        ({ isEditing, selectedRowIds }) => {
          if (!isEditing || selectedRowIds.length === 0) {
            return;
          }

          const trajectory = this.trajectoryService.mainFactTrajectory;

          if (!trajectory) {
            return;
          }

          const next = selectedRowIds.filter((rowId) => {
            const point = trajectory.pointsMap.get(String(rowId));

            return point ? !trajectory.isPointLocked(point) : false;
          });

          if (next.length !== selectedRowIds.length) {
            this.setSelectedRowIds(next);
          }
        },
        { fireImmediately: true },
      ),
      reaction(
        () => ({
          selectedRows: this.selectedRowIds,
          isLocked: this.isUiLocked,
          callback: this.onRowDeleteButtonStateChange,
        }),
        ({ selectedRows, isLocked, callback }) => {
          if (!callback) {
            return;
          }

          callback({
            onDeleteRows: this.deleteSelectedRows,
            isDisabled: selectedRows.length === 0 || isLocked,
          });
        },
        { fireImmediately: true },
      ),
    );

    return () => {
      this.disposeReactions();
    };
  };

  @action.bound
  private disposeReactions(): void {
    this.disposers.forEach((disposeFn) => disposeFn());
    this.disposers = [];
  }

  @action.bound
  dispose(): void {
    this.disposeReactions();
    this.onRowDeleteButtonStateChange?.({ onDeleteRows: null, isDisabled: true });
    this.onRowDeleteButtonStateChange = null;
    this.onStateChange = undefined;
    this.graphStore = null;
    this.selectedRowIds = [];
  }
}
